export function isMissingAvatar(avatarUrl) {
  return !avatarUrl || avatarUrl.includes('missing')
}

export function resolveAvatarUrl(avatarUrl, missingImg) {
  if (isMissingAvatar(avatarUrl)) {
    return missingImg
  }
  return avatarUrl
}

export function fallbackAvatarOnError(event, missingImg) {
  if (!missingImg || !event || !event.target) return
  if (event.target.src === missingImg) return
  event.target.src = missingImg
  event.target.onerror = null
}

export function isUserOnline(lastSeen, forceOnline = false) {
  if (forceOnline) return true
  if (!lastSeen) return false

  const seen = new Date(lastSeen)
  const now = new Date()
  return (now - seen) < 15 * 60 * 1000
}

export function formatLocation(location) {
  if (!location) return null

  const parts = location.split(',').map((part) => part.trim()).filter(Boolean)
  if (parts.length === 0) return null
  if (parts.length <= 2) return parts.join(', ')
  return `${parts[0]}, ${parts[parts.length - 1]}`
}

export function formatLocationCity(location) {
  if (!location) return null

  const parts = location.split(',')
  const city = parts[0] ? parts[0].trim() : ''
  return city || null
}

export function languageAbbrev(language) {
  if (!language) return ''

  if (language.locale) {
    return language.locale.slice(0, 2).toUpperCase()
  }

  if (language.language) {
    return language.language.slice(0, 2).toUpperCase()
  }

  return ''
}

export function languageFlagUrl(language) {
  if (!language) return null
  return language.flag_image || language.flag_image_url || null
}

export function languageFlagClass(language) {
  if (!language || !language.flag) return 'flag-unknown'
  return language.flag
}

export function isNativeLevel(level) {
  return Number(level) === 5
}

export function getUserAge(birthday) {
  if (!birthday) return null

  const bd = new Date(birthday)
  if (Number.isNaN(bd.getTime())) return null

  const today = new Date()
  let age = today.getFullYear() - bd.getFullYear()
  const monthDiff = today.getMonth() - bd.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < bd.getDate())) {
    age -= 1
  }

  return age
}

export function formatNameWithAge(name, birthday) {
  const age = getUserAge(birthday)
  if (age == null) return name
  return `${name}, ${age}`
}

export function languageLabel(language) {
  if (!language) return ''
  return language.native || language.language || ''
}

export function getLearningLanguages(languagesUsers = []) {
  if (!Array.isArray(languagesUsers)) return []

  return languagesUsers
    .filter((entry) => !isNativeLevel(entry.level))
    .map((entry) => entry.language)
    .filter(Boolean)
}

export function formatLearningLine(languagesUsers = []) {
  const learning = getLearningLanguages(languagesUsers)
  const name = languageLabel(learning[0])
  if (!name) return null
  return `Learning ${name}`
}

export function formatSpeaksLine(languagesUsers = []) {
  const native = getNativeLanguages(languagesUsers)
  const names = native.map(languageLabel).filter(Boolean)
  if (names.length === 0) return null
  return `Speaks ${names.join(', ')}`
}

export function truncateText(text, maxLength = 120) {
  if (!text) return null

  const trimmed = text.trim()
  if (!trimmed) return null
  if (trimmed.length <= maxLength) return trimmed

  return `${trimmed.slice(0, maxLength).trim()}…`
}

const LEVEL_SHORT_LABELS = ['', 'Beg', 'Beg', 'Int', 'Adv', 'Native']

export function getCompactLanguageLabel(language, level) {
  const levelLabel = LEVEL_SHORT_LABELS[Number(level)] || ''
  let langLabel = ''

  if (language && language.locale && /zh|cn|ko|ja/i.test(language.locale)) {
    langLabel = language.native || language.language || language.locale.slice(0, 2).toUpperCase()
  } else if (language && language.locale) {
    langLabel = language.locale.slice(0, 2).toUpperCase()
  } else if (language && language.language) {
    const name = language.language.trim()
    langLabel = name.length <= 4 ? name : name.slice(0, 2).toUpperCase()
  }

  return `${levelLabel} ${langLabel}`.trim()
}

export function getLastSeenDateLabel(lastSeen) {
  if (!lastSeen) return null

  const seen = new Date(lastSeen)
  if (Number.isNaN(seen.getTime())) return null

  return seen.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()
}

export function formatActivityLabel(label) {
  if (!label) return null
  return label.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
}

export function getActivityBadgeLabel(lastSeen) {
  if (!lastSeen) return null

  const seen = new Date(lastSeen)
  if (Number.isNaN(seen.getTime())) return null

  const now = new Date()
  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

  const seenDay = startOfDay(seen)
  const today = startOfDay(now)
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (seenDay.getTime() === today.getTime()) return 'ACTIVE TODAY'
  if (seenDay.getTime() === yesterday.getTime()) return 'ACTIVE YESTERDAY'

  const weekAgo = new Date(today)
  weekAgo.setDate(weekAgo.getDate() - 7)
  if (seen >= weekAgo) return 'ACTIVE THIS WEEK'

  return seen.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()
}

const LANGUAGE_LEVELS = ['', 'Just Started', 'Beginner', 'Intermediate', 'Advanced', 'Native']

export function getLanguageLevelLabel(level) {
  const numericLevel = Number(level)
  if (numericLevel >= 1 && numericLevel <= 5) {
    return LANGUAGE_LEVELS[numericLevel]
  }
  return ''
}

export function getLanguageBadgeEntries(languagesUsers = []) {
  if (!Array.isArray(languagesUsers)) return []

  return languagesUsers
    .filter((entry) => entry.language)
    .map((entry, index) => {
      const level = Number(entry.level)
      const levelLabel = getLanguageLevelLabel(level)
      const langName = languageLabel(entry.language)
      const isNative = isNativeLevel(level)
      const lang = entry.language
      const langKey = entry.language_id != null
        ? entry.language_id
        : lang != null && lang.id != null
          ? lang.id
          : lang != null && lang.flag != null
            ? lang.flag
            : lang != null && lang.language != null
              ? lang.language
              : index

      return {
        key: `${langKey}-${level}`,
        label: `${levelLabel} ${langName}`.trim(),
        compactLabel: getCompactLanguageLabel(entry.language, level),
        flagHtml: entry.language.flag_html,
        isNative,
        level,
      }
    })
    .sort((a, b) => {
      if (a.isNative !== b.isNative) return a.isNative ? -1 : 1
      return b.level - a.level
    })
}

export function getNativeLanguages(languagesUsers = []) {
  if (!Array.isArray(languagesUsers)) return []

  return languagesUsers
    .filter((entry) => isNativeLevel(entry.level))
    .map((entry) => entry.language)
    .filter(Boolean)
}

export function getConnectPartner(connect, currentUserId) {
  if (!connect) return null
  return currentUserId === connect.requestor_id ? connect.requestee : connect.requestor
}

export function userSpeaksLanguageNatively(languagesUsers, languageKey) {
  if (!languageKey || !Array.isArray(languagesUsers)) return false

  return languagesUsers.some((entry) => {
    if (!isNativeLevel(entry.level) || !entry.language) return false
    const lang = entry.language
    return lang.flag === languageKey || lang.language === languageKey || lang.locale === languageKey
  })
}

export function getLearningLanguageTabs(languagesUsers = [], connectedUsers = [], currentUserId) {
  if (!Array.isArray(languagesUsers)) return []

  return languagesUsers
    .filter((entry) => !isNativeLevel(entry.level) && entry.language)
    .map((entry) => {
      const lang = entry.language
      const key = lang.flag || lang.language
      const count = connectedUsers.filter((connect) => {
        const partner = getConnectPartner(connect, currentUserId)
        return userSpeaksLanguageNatively(partner && partner.languages_users, key)
      }).length

      return {
        key,
        label: languageLabel(lang),
        flagHtml: lang.flag_html,
        count,
      }
    })
}

export function filterConnects(connectedUsers = [], currentUserId, { searchQuery = '', languageKey = 'all' } = {}) {
  if (!Array.isArray(connectedUsers)) return []

  const query = searchQuery.trim().toLowerCase()

  return connectedUsers.filter((connect) => {
    const partner = getConnectPartner(connect, currentUserId)
    if (!partner) return false

    if (languageKey !== 'all' && !userSpeaksLanguageNatively(partner.languages_users, languageKey)) {
      return false
    }

    if (!query) return true

    const name = (partner.preferred_name || '').toLowerCase()
    const location = (partner.location || '').toLowerCase()
    const bio = (partner.bio || '').toLowerCase()
    return name.includes(query) || location.includes(query) || bio.includes(query)
  })
}

export function getLanguagePair(languagesUsers = []) {
  const native = languagesUsers
    .filter((entry) => isNativeLevel(entry.level))
    .map((entry) => entry.language)
    .filter(Boolean)

  const learning = languagesUsers
    .filter((entry) => entry.level < 5)
    .map((entry) => entry.language)
    .filter(Boolean)

  const pair = []
  if (native[0]) pair.push(native[0])
  if (learning[0] && (!native[0] || learning[0].language !== native[0].language)) {
    pair.push(learning[0])
  }
  return pair
}
